import { useCallback } from "react"

import {
    QueryClient,
    type UseQueryOptions,
    type UseQueryResult,
    useQueries,
    useQuery,
    useQueryClient
} from "@tanstack/react-query"
import { AxiosError, HttpStatusCode } from "axios"
import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"

import type { TranslationObj } from "$/utils/lang"

import { hours, seconds } from "@/3514/utils"
import { type TEmptyAsyncCallback } from "@/shared/types/functions"

import { ECoachingChatQueryKey as EQueryKey } from "../../config"
import type {
    ICoachingChatConversationQueryModel as IConversationModel,
    ICoachingChatDisclaimerQueryDto as IDisclaimerQueryDto,
    ICoachingChatNewMessagesQueryModel as INewMessagesModel
} from "../../types"

import { coachingChatQueriesAdapters as adapters } from "./coaching-chat.api.queries.adapters"
import { coachingChatQueriesFetchers as fetchers } from "./coaching-chat.api.queries.fetchers"

type TConversationHooksParams = {
    conversationId: number
    conversationQueryParams?: Pick<UseQueryOptions, "enabled">
    newMessagesQueryParams?: Pick<UseQueryOptions, "enabled" | "refetchInterval">
}

type TConversationQuery = UseQueryResult<IConversationModel, AxiosError>
type TNewMessagesQuery = UseQueryResult<INewMessagesModel, AxiosError>
type TDisclaimerQuery = UseQueryResult<TranslationObj, AxiosError>

function useConversationHooks({
    conversationId,
    conversationQueryParams: conversationParams,
    newMessagesQueryParams: newMessagesParams
}: TConversationHooksParams): ReturnType<
    (params: TConversationHooksParams) => [TConversationQuery, TNewMessagesQuery]
> {
    return useQueries<[TConversationQuery, TNewMessagesQuery]>({
        queries: [
            {
                queryKey: EQueryKey.Conversation(conversationId),
                queryFn: fetchers.conversationFetcher,
                select: adapters.conversationAdapter,
                notifyOnChangeProps: ["data", "error", "isFetching"],
                staleTime: hours(2),
                gcTime: hours(1),
                retry: (failureCount: number, error: AxiosError): boolean =>
                    eq(error.status, HttpStatusCode.InternalServerError) ? failureCount <= 1 : false,
                retryDelay: seconds(3),
                refetchOnWindowFocus: false,
                refetchOnMount: false,
                throwOnError: false,
                ...conversationParams
            },
            {
                queryKey: EQueryKey.NewMessages(conversationId),
                queryFn: fetchers.newMessagesFetcher,
                select: adapters.newMessagesAdapter,
                notifyOnChangeProps: ["data", "error"],
                retry: (failureCount: number, error: AxiosError): boolean =>
                    eq(error.status, HttpStatusCode.NotFound) ? false : failureCount <= 1,
                retryDelay: seconds(2),
                staleTime: 0,
                gcTime: 0,
                throwOnError: false,
                ...newMessagesParams
            }
        ]
    })
}

function useConversationQueryRefetch(
    conversationId: number
): ReturnType<(conversationId: number) => TEmptyAsyncCallback> {
    const qc: QueryClient = useQueryClient()

    return useCallback(async (): Promise<void> => {
        if (isNull(conversationId)) return

        void qc.resetQueries({
            queryKey: EQueryKey.Conversation(conversationId),
            type: "all",
            exact: true,
            stale: false
        })

        if (isEmpty(qc.getQueryData(EQueryKey.Conversation(conversationId))))
            void qc.fetchQuery({
                queryKey: EQueryKey.Conversation(conversationId) as never,
                queryFn: fetchers.conversationFetcher
            })
        else
            void qc.refetchQueries({
                queryKey: EQueryKey.Conversation(conversationId),
                type: "all",
                exact: true,
                stale: false
            })
    }, [conversationId, qc])
}

function useDisclaimerHook(): ReturnType<() => TDisclaimerQuery> {
    return useQuery<IDisclaimerQueryDto, AxiosError, TranslationObj>({
        queryKey: EQueryKey.Disclaimer(),
        queryFn: fetchers.legaliseFetcher,
        select: adapters.disclaimerAdapter,
        enabled: true,
        throwOnError: false,
        notifyOnChangeProps: ["data", "isFetching", "isError", "error", "dataUpdatedAt", "refetch"],
        retry: (failureCount: number, error: AxiosError): boolean =>
            eq(error.status, HttpStatusCode.NotFound) ? false : failureCount <= 1,
        retryDelay: seconds(2),
        gcTime: 0,
        staleTime: 0
    })
}

const queries = { useConversationHooks, useDisclaimerHook }

export { queries as coachingChatQueries, useConversationQueryRefetch as useCoachingChatConversationQueryRefetch }
export type {
    TConversationQuery as TCoachingChatConversationQuery,
    TNewMessagesQuery as TCoachingChatNewMessagesQuery,
    TDisclaimerQuery as TCoachingChatDisclaimerQuery
}
